import { clsx, type ClassValue } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

export const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-green-500/20 text-green-400 border-green-500/30',
};

export const AGENT_TYPE_COLORS: Record<string, string> = {
  triager: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  architect: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  reviewer: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  builder: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  tester: 'bg-green-500/20 text-green-400 border-green-500/30',
  deployer: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  monitor: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

export const PHASE_COLORS: Record<string, string> = {
  backlog: 'bg-gray-500/20 text-gray-400',
  triage: 'bg-yellow-500/20 text-yellow-400',
  planning: 'bg-blue-500/20 text-blue-400',
  'plan-review': 'bg-indigo-500/20 text-indigo-400',
  implementation: 'bg-purple-500/20 text-purple-400',
  testing: 'bg-green-500/20 text-green-400',
  review: 'bg-cyan-500/20 text-cyan-400',
  deployment: 'bg-orange-500/20 text-orange-400',
  monitoring: 'bg-rose-500/20 text-rose-400',
  done: 'bg-emerald-500/20 text-emerald-400',
};

export const STATUS_COLORS: Record<string, string> = {
  draft: 'text-gray-400',
  triaging: 'text-yellow-400',
  planning: 'text-blue-400',
  'awaiting-approval': 'text-orange-400',
  implementing: 'text-purple-400',
  reviewing: 'text-cyan-400',
  ready: 'text-green-400',
  merged: 'text-emerald-400',
  archived: 'text-gray-500',
};

export const PHASE_COLOR_PALETTE = [
  'gray', 'red', 'orange', 'amber', 'yellow', 'lime', 'green',
  'emerald', 'teal', 'cyan', 'blue', 'indigo', 'violet', 'purple',
  'fuchsia', 'pink', 'rose'
] as const;

export type PhaseColorKey = typeof PHASE_COLOR_PALETTE[number];

export const PHASE_COLOR_MAP: Record<string, { dot: string; badge: string }> = {
  gray:    { dot: 'bg-gray-500',    badge: 'bg-gray-500/20 text-gray-400' },
  red:     { dot: 'bg-red-500',     badge: 'bg-red-500/20 text-red-400' },
  orange:  { dot: 'bg-orange-500',  badge: 'bg-orange-500/20 text-orange-400' },
  amber:   { dot: 'bg-amber-500',   badge: 'bg-amber-500/20 text-amber-400' },
  yellow:  { dot: 'bg-yellow-500',  badge: 'bg-yellow-500/20 text-yellow-400' },
  lime:    { dot: 'bg-lime-500',    badge: 'bg-lime-500/20 text-lime-400' },
  green:   { dot: 'bg-green-500',   badge: 'bg-green-500/20 text-green-400' },
  emerald: { dot: 'bg-emerald-500', badge: 'bg-emerald-500/20 text-emerald-400' },
  teal:    { dot: 'bg-teal-500',    badge: 'bg-teal-500/20 text-teal-400' },
  cyan:    { dot: 'bg-cyan-500',    badge: 'bg-cyan-500/20 text-cyan-400' },
  blue:    { dot: 'bg-blue-500',    badge: 'bg-blue-500/20 text-blue-400' },
  indigo:  { dot: 'bg-indigo-500',  badge: 'bg-indigo-500/20 text-indigo-400' },
  violet:  { dot: 'bg-violet-500',  badge: 'bg-violet-500/20 text-violet-400' },
  purple:  { dot: 'bg-purple-500',  badge: 'bg-purple-500/20 text-purple-400' },
  fuchsia: { dot: 'bg-fuchsia-500', badge: 'bg-fuchsia-500/20 text-fuchsia-400' },
  pink:    { dot: 'bg-pink-500',    badge: 'bg-pink-500/20 text-pink-400' },
  rose:    { dot: 'bg-rose-500',    badge: 'bg-rose-500/20 text-rose-400' },
};

export function getPhaseColor(colorKey?: string) {
  return PHASE_COLOR_MAP[colorKey || 'gray'] || PHASE_COLOR_MAP.gray;
}

/**
 * Eight preset board colors for the Command Center swatch picker.
 * Keyed by the same palette names as PHASE_COLOR_MAP so swatch previews reuse `dot` classes.
 */
export const BOARD_COLOR_KEYS = [
  'red', 'orange', 'amber', 'green', 'teal', 'blue', 'purple', 'pink',
] as const;

export type BoardColorKey = typeof BOARD_COLOR_KEYS[number];

/** Hex values used as inline `borderLeftColor` — hex stays stable across Tailwind rebuilds. */
export const BOARD_COLOR_HEX: Record<string, string> = {
  red:    '#ef4444',
  orange: '#f97316',
  amber:  '#f59e0b',
  green:  '#22c55e',
  teal:   '#14b8a6',
  blue:   '#3b82f6',
  purple: '#a855f7',
  pink:   '#ec4899',
  gray:   '#6b7280',
};

export function getBoardColorHex(key?: string): string {
  return BOARD_COLOR_HEX[key || 'gray'] ?? BOARD_COLOR_HEX.gray;
}
